import React, { useState, useEffect } from "react";
import { Container, Row, Col, Table, Form, Spinner } from "react-bootstrap";
import { FaEdit, FaTrash, FaPlus, FaEye, FaHistory } from "react-icons/fa";
import { Link } from "react-router-dom";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../Components/Pagination";
import api from "../services/api";
import "./CustomerList.css";

const CustomerList = () => {
  const [search, setSearch] = useState("");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch customers from API
  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Note: The customer_list.php expects JSON input, so we need to send an empty object
      const response = await api.post("/customer_list.php", {});
      
      if (response.data.head.code === 200) {
        // Transform the data to match frontend field names
        const transformedData = response.data.body.map(customer => ({
          id: customer.id,
          name: customer.name,
          mobile: customer.mobile,
          gst_number: customer.gst_no, // Note: backend uses 'gst_no', frontend uses 'gst_number'
          state: customer.state,
          city: customer.city,
          pin_code: customer.pin_code,
          address: customer.address
        }));
        
        setCustomers(transformedData);
      } else {
        setError(response.data.head.msg || "Failed to fetch customers");
      }
    } catch (err) {
      console.error("Error fetching customers:", err);
      setError("Failed to load customers. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Delete customer handler with API integration
  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete ${name}?`)) {
      return;
    }

    try {
      const response = await api.post("/customer_delete.php", { id });
      
      if (response.data.head.code === 200) {
        // Remove from local state
        setCustomers(customers.filter((c) => c.id !== id));
        alert("Customer deleted successfully!");
      } else {
        alert("Failed to delete customer: " + response.data.head.msg);
      }
    } catch (err) {
      console.error("Error deleting customer:", err);
      alert("Failed to delete customer. Please try again.");
    }
  };

  // Filter customers based on search
  const filteredCustomers = customers.filter((c) => {
    if (!search) return true;
    const query = search.toLowerCase();
    return (
      String(c.id).toLowerCase().includes(query) ||
      (c.name && c.name.toLowerCase().includes(query)) ||
      (c.state && c.state.toLowerCase().includes(query)) ||
      (c.city && c.city.toLowerCase().includes(query)) ||
      (c.mobile && c.mobile.includes(query)) ||
      (c.gst_number && c.gst_number.toLowerCase().includes(query))
    );
  });

  // Pagination hook
  const {
    currentPage,
    totalPages,
    currentItems: currentCustomers,
    handlePageChange,
    indexOfFirstItem
  } = usePagination(filteredCustomers, 10); // 10 items per page

  if (loading) {
    return (
      <Container fluid className="outer-container my-3">
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">Loading customers...</p>
        </div>
      </Container>
    );
  }
{/*
  if (error) {
    return (
      <Container fluid className="outer-container my-3">
        <div className="alert alert-danger" role="alert">
          {error}
          <button className="btn btn-sm btn-outline-danger ms-3" onClick={fetchCustomers}>
            Retry
          </button>
        </div>
      </Container>
    );
  }
*/}
  return (
    <Container fluid className="outer-container my-3">
      <h2 className="text-center mb-3">Customer Management</h2>
      
      {/* Search and Add Customer Section */}
      <Row className="mb-3 align-items-center">
        <Col xs={12} md={6} lg={4} className="mb-2 mb-md-0">
          <Form.Control
            placeholder="Search by name, mobile"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Col>

        <Col xs={12} md={6} lg={8} className="d-flex justify-content-md-end">
          <Link
            to="/addcustomer"
            className="btn-top d-inline-flex align-items-center"
          >
            <FaPlus className="me-2" /> Add Customer
          </Link>
        </Col>
      </Row>

      {/* Table Section */}
      <div className="table-pagination-wrapper">
        <div className="table-pagination-content">
          <Table bordered responsive className="table-striped customer-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Customer Name</th>
                <th>Mobile</th>
                {/* <th>GST Number</th>
                <th>State</th>
                <th>City</th>
                <th>Pin Code</th>
                <th>Order History</th> */}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentCustomers.map((customer, index) => (
                <tr key={customer.id}>
                  <td>{indexOfFirstItem + index + 1}</td>
                  <td>{customer.name}</td>
                  <td>{customer.mobile}</td>
                  {/* <td>{customer.gst_number || "N/A"}</td>
                  <td>{customer.state}</td>
                  <td>{customer.city || "N/A"}</td>
                  <td>{customer.pin_code || "N/A"}</td>
                  <td>
                    <Link
                      to={`/customerorders/${customer.id}`}
                      className="order-history-link"
                      title="View Order History"
                    >
                      <FaHistory className="history-icon" />
                    </Link>
                  </td> */}
                  <td>
                    <div className="action-icons">
                      {/* <Link
                        to={`/viewcustomer/${customer.id}`}
                        className="me-2"
                        title="View Customer"
                      >
                        <FaEye className="view-icon" />
                      </Link> */}
                      <Link
                        to={`/editcustomer/${customer.id}`}
                        className="me-2"
                        title="Edit Customer"
                      >
                        <FaEdit className="edit-icon" />
                      </Link>
                      <FaTrash
                        className="delete-icon"
                        title="Delete Customer"
                        onClick={() => handleDelete(customer.id, customer.name)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
              {currentCustomers.length === 0 && (
                <tr>
                  <td colSpan="9" className="text-center">
                    {search ? "No customers found matching your search" : "No customers found"}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
    </Container>
  );
};

export default CustomerList;